import Selectr from "mobius1-selectr"
import Pikaday from "pikaday"
import { queryStringToProps, getDates } from "../formUtils.js"

export default function init() {
  document.querySelectorAll("select").forEach(element => {
    const title = element.attributes.placeholder ? element.attributes.placeholder.value : null
    const selectr = new Selectr(element, {
      searchable: !!element.attributes.searchable,
      placeholder: title,
    })

    // set this as an attribute against the field to expose functions
    element.selectr = selectr

    selectr.on("selectr.select", option => {
      document.querySelectorAll("button.selectr-tag-remove").forEach(button => {
        button.setAttribute("aria-label", `delete current selection - ${option.value}`)
        button.addEventListener(
          "keydown",
          e => {
            if (e.keyCode == 13) {
              e.preventDefault()
              e.stopPropagation()
              e.target.click()
            }
          },
          false
        )
      })
    })

    selectr.on("selectr.init", function() {
      // bubble event to DOM element so it can be listened to elsewhere
      element.dispatchEvent(new Event("selectr.init"))

      const button = selectr.container.querySelector(".selectr-selected")

      if (button) {
        const ariaLabel = selectr.config.placeholder ? selectr.config.placeholder.toLowerCase() : "Select option"
        button.setAttribute("role", "button")
        button.setAttribute("aria-label", `Select ${ariaLabel}`)
        button.setAttribute("aria-pressed", "false")

        const iconEl = document.createElement("i")
        iconEl.classList.add("material-icons")
        iconEl.classList.add("selectr-arrow")
        iconEl.setAttribute("aria-hidden", "true")
        iconEl.textContent = "keyboard_arrow_down"

        button.prepend(iconEl)
      }

      if (detectMob()) {
        selectr.el.setAttribute("tabindex", "0")
        selectr.el.setAttribute("aria-hidden", "false")
      } else {
        selectr.el.setAttribute("tabindex", "-1")
        selectr.el.setAttribute("aria-hidden", "true")
      }

      if (selectr.input) {
        selectr.input.ariaLabel = title ? `Search for ${title}` : `Search`
      }

      // prevent scroll when opening dropdown
      selectr.selected.addEventListener("keydown", e => {
        // spacebar
        if (e.keyCode == 32) {
          e.preventDefault()
        } else if (e.keyCode == 13) {
          e.preventDefault()
          e.stopPropagation()
          selectr.open()
        }
      })

      if (selectr.input) {
        selectr.input.removeAttribute("role")
        selectr.input.addEventListener("keydown", e => {
          if (e.keyCode == 13) {
            e.preventDefault()
            e.stopPropagation()
          }
        })
      }

      selectr.items.forEach(option => {
        // allow tabbing
        option.setAttribute("tabindex", 0)

        // close dropdown when tabbing out of it
        option.addEventListener("blur", () => {
          setTimeout(() => {
            const activeElement = document.activeElement
            if (!selectr.container.contains(activeElement)) {
              selectr.close()
              activeElement.focus()
            }
          }, 1)
        })

        // add arrow navigation
        option.addEventListener("keydown", e => {
          if (e.keyCode == 13) {
            e.preventDefault()
            e.stopPropagation()
            option.click()
          }

          if (e.keyCode == 32) {
            // spacebar
            e.preventDefault()
            option.click()
          }

          // arrow down
          if (e.keyCode == 40) {
            e.preventDefault()
            e.stopPropagation()
            const nextOption = option.nextElementSibling
              ? option.nextElementSibling
              : option.parentElement.firstElementChild
            nextOption.focus()
          }

          // arrow up
          if (e.keyCode == 38) {
            e.preventDefault()
            e.stopPropagation()
            const nextOption = option.previousElementSibling
              ? option.previousElementSibling
              : option.parentElement.lastElementChild
            nextOption.focus()
          }
        })
      })
    })

    selectr.on("selectr.open", () => {
      const button = selectr.container.querySelector(".selectr-selected")

      if (button) {
        button.setAttribute("aria-pressed", "true")
      }
    })

    selectr.on("selectr.close", () => {
      const button = selectr.container.querySelector(".selectr-selected")

      if (button) {
        button.setAttribute("aria-pressed", "false")
      }
    })
  })

  // Prefer to use a text input with class 'date-picker',
  // so can set input value to a custom date format "dd/mm/yyyy"
  document.querySelectorAll("input.date-picker").forEach(element => {
    if (!element.pickaday) {
      createDatePicker(element)
    }
  })

  // Also support userforms that use a date input,
  // these set the value using standard html format "yyyy-mm-dd"
  document.querySelectorAll("input[type='date']").forEach(element => {
    let parent = element.parentNode
    let doDatepicker = true

    while (parent) {
      if (parent.classList && parent.classList.contains("search__dates")) {
        doDatepicker = false
        parent = null
      } else {
        parent = parent.parentNode
      }
    }

    if (doDatepicker && !element.pickaday) {
      element.setAttribute("type", "text")
      element.classList.add("date-picker")
      createDatePicker(element)
    }
  })

  function createDatePicker(element) {
    element.placeholder = "dd/mm/yyyy"
    const label = element.getAttribute("aria-label")
    // eslint-disable-next-line no-use-before-define
    const arialabel = label
      ? "Use the arrow keys to pick a " + label.toLowerCase()
      : "Use the arrow keys to select a date"
    // eslint-disable-next-line no-use-before-define
    const dateFromUrl = { ...queryStringToProps(window.location.search) }
    // eslint-disable-next-line no-use-before-define
    const defaultDate =
      label === "start date"
        ? dateFromUrl["from-date"] && getDates(dateFromUrl["from-date"])
        : dateFromUrl["to-date"] && getDates(dateFromUrl["to-date"])

    let pickaday = new Pikaday({
      field: element,
      ariaLabel: arialabel,
      setDefaultDate: true,
      format: "DD/MM/YYYY",
      showDaysInNextAndPreviousMonths: true,
      i18n: {
        previousMonth: "Previous Month",
        nextMonth: "Next Month",
        months: [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December",
        ],
        weekdays: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        weekdaysShort: ["S", "M", "T", "W", "T", "F", "S"],
      },
    })
    element.pickaday = pickaday

    if (defaultDate) {
      pickaday.setDate(defaultDate)
    }
    // <i class="material-icons">date_range</i>
    const icon = document.createElement("i")
    icon.innerText = "date_range"
    icon.classList.add("material-icons")
    // insertAfter
    element.parentNode.insertBefore(icon, element.nextSibling)
  }
}

const detectMob = () => {
  const toMatch = [/Android/i, /webOS/i, /iPhone/i, /iPad/i, /iPod/i, /BlackBerry/i, /Windows Phone/i]

  return toMatch.some(toMatchItem => {
    return navigator.userAgent.match(toMatchItem)
  })
}
